import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type LoginResponse } from '../api/auth'
import {
  IMPORT_KIND_LABELS,
  importsApi,
  type ImportApplyResult,
  type ImportBatch,
  type ImportBatchDependencies,
  type ImportKind,
  type ImportMappableField,
  type ImportPreview,
  type ImportRowOutcome,
} from '../api/imports'
import { importMappingsApi, type ImportMapping } from '../api/importMappings'
import { useFeatureFlag } from '../lib/featureFlags'
import { HubTypesImportPanel, ImportRegistryPanel, ProductImportPanel } from '../components/ImportHubPanels'
import { CSV_ACCEPT, EXCEL_ACCEPT, readTabularFile } from '../lib/spreadsheet'
import {
  FormGrid,
  Button, Callout, Card, CardContent, CardHeader, Checkbox, DataTable, Page, PageHeader, LoadingState, Select,
  StatusPill, Tabs, TextField,
} from '../components/ui'

/**
 * F-068 (TASK-0322, REQ-238-001 / REQ-239-001 / REQ-240-001) — pagina "Importazioni".
 *
 * Un solo flusso per lead e clienti (spec §239): file -> riconoscimento colonne -> mappatura ->
 * anteprima -> validazione -> duplicati -> errori -> conferma. L'anteprima non scrive nulla: i dati
 * entrano solo con "Conferma importazione", che crea un lotto con ID durevole. La tab "Storico lotti"
 * mostra i lotti e, per l'Admin, "Annulla lotto": ammesso solo se nessun dato dipende dai record del
 * lotto (altrimenti il server risponde 409 e qui si vedono i conteggi per area). Solo CSV, niente Excel.
 *
 * Tutta la validazione, la normalizzazione e la scoperta dei duplicati sono del server; questa pagina
 * mostra soltanto ciò che l'anteprima restituisce.
 */

interface Props {
  auth: LoginResponse
}

type Step = 'upload' | 'mapping' | 'preview' | 'done'

const MAX_FILE_BYTES = 5 * 1024 * 1024
const NO_FIELD = ''

const OUTCOME_LABELS: Record<ImportRowOutcome, string> = {
  Importable: 'Importabile',
  MissingRequiredField: 'Campo obbligatorio mancante',
  InvalidValue: 'Valore non valido',
  Duplicate: 'Duplicato',
}

const OUTCOME_TONES: Record<ImportRowOutcome, 'success' | 'error' | 'warning'> = {
  Importable: 'success',
  MissingRequiredField: 'error',
  InvalidValue: 'error',
  Duplicate: 'warning',
}

const PROBLEM_LABELS: Record<string, string> = {
  ColumnCountMismatch: 'Numero di colonne diverso dall’intestazione',
  MissingContactChannel: 'Serve almeno telefono o email',
  MissingSource: 'Fonte obbligatoria',
  InvalidSource: 'Fonte non riconosciuta',
  InvalidLeadType: 'Tipo lead non valido',
  InvalidPriority: 'Priorità non valida',
  InvalidTemperature: 'Temperatura non valida',
  InvalidEstimatedValue: 'Valore stimato non valido',
  MissingName: 'Serve almeno nome, cognome o ragione sociale',
  InvalidCustomerType: 'Tipo cliente non valido',
  InvalidEmail: 'Email non valida',
  InvalidPhone: 'Telefono non valido',
  InvalidMobile: 'Cellulare non valido',
  InvalidFiscalCode: 'Codice fiscale non valido',
  InvalidVatNumber: 'Partita IVA non valida (11 cifre)',
  InvalidPostalCode: 'CAP non valido (5 cifre)',
  InvalidProvince: 'Provincia non valida (sigla di 2 lettere)',
  InvalidPec: 'PEC non valida',
  InvalidSdiCode: 'Codice SDI non valido',
  ValueTooLong: 'Valore troppo lungo',
}

const FIELD_LABELS: Record<string, string> = {
  vatNumber: 'Partita IVA',
  fiscalCode: 'Codice fiscale',
  email: 'Email',
  phone: 'Telefono',
}

function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

function fmtDateTime(s: string | null | undefined): string {
  return s ? new Date(s).toLocaleString('it-IT') : '—'
}

/** Intestazioni del file: prima riga, delimitatore `;` o `,` (quello più frequente fuori dalle virgolette). */
function readHeaders(csv: string): string[] {
  const text = csv.charCodeAt(0) === 0xfeff ? csv.slice(1) : csv
  const firstLine = text.split(/\r\n|\r|\n/, 1)[0] ?? ''
  let semicolons = 0
  let commas = 0
  let inQuotes = false
  for (const c of firstLine) {
    if (c === '"') inQuotes = !inQuotes
    else if (!inQuotes && c === ';') semicolons++
    else if (!inQuotes && c === ',') commas++
  }
  const delimiter = semicolons > commas ? ';' : ','
  const headers: string[] = []
  let current = ''
  inQuotes = false
  for (const c of firstLine) {
    if (c === '"') inQuotes = !inQuotes
    else if (!inQuotes && c === delimiter) {
      headers.push(current.trim())
      current = ''
    } else current += c
  }
  headers.push(current.trim())
  return headers.filter(h => h.length > 0)
}

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Errore'
}

type ImportsTab = 'import' | 'history' | 'registry' | 'products' | 'hubtypes'

export function ImportsPage({ auth }: Props) {
  const [tab, setTab] = useState<ImportsTab>('import')
  // C-17: con il flag `import.hub` la pagina riunisce il registro di tutti i lotti e l'import dei prodotti.
  const hub = useFeatureFlag('import.hub')
  const hubExtended = useFeatureFlag('import.hub_esteso')
  const items = [
    { id: 'import', label: 'Nuova importazione' },
    { id: 'history', label: 'Storico lotti' },
    ...(hub ? [{ id: 'registry', label: 'Registro unico' }, { id: 'products', label: 'Prodotti' }] : []),
    ...(hub && hubExtended ? [{ id: 'hubtypes', label: 'Spese, crediti, provvigioni' }] : []),
  ]
  return (
    <Page>
      <PageHeader
        title="Importazioni"
        icon="upload_file"
        tabs={<Tabs aria-label="Importazioni" value={tab} onChange={id => setTab(id as ImportsTab)} items={items} />}
      />
      {tab === 'import' && <ImportWizard auth={auth} onDone={() => setTab('history')} />}
      {tab === 'history' && <HistoryTab auth={auth} />}
      {hub && tab === 'registry' && <ImportRegistryPanel />}
      {hub && tab === 'products' && <ProductImportPanel />}
      {hub && hubExtended && tab === 'hubtypes' && <HubTypesImportPanel />}
    </Page>
  )
}

/* ─────────────────────────── Procedura guidata ─────────────────────────── */

function ImportWizard({ auth, onDone }: { auth: LoginResponse; onDone: () => void }) {
  const importExcel = useFeatureFlag('import.excel')
  const [step, setStep] = useState<Step>('upload')
  const [kind, setKind] = useState<ImportKind>('customers')
  const [fields, setFields] = useState<ImportMappableField[]>([])
  const [requiredRule, setRequiredRule] = useState('')
  const [saved, setSaved] = useState<ImportMapping[]>([])
  const [savedId, setSavedId] = useState('')
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportApplyResult | null>(null)
  const [outcomeFilter, setOutcomeFilter] = useState<ImportRowOutcome | ''>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [mapName, setMapName] = useState('')
  const [mapShared, setMapShared] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const loadKindData = useCallback(async (k: ImportKind) => {
    try {
      const [f, m] = await Promise.all([importsApi.mappableFields(k), importMappingsApi.list(k)])
      setFields(f.fields)
      setRequiredRule(f.requiredRule ?? f.requiredContactChannelRule ?? '')
      setSaved(m.items)
    } catch (e) {
      setError(errMessage(e))
    }
  }, [])

  useEffect(() => {
    void loadKindData(kind)
  }, [kind, loadKindData])

  const reset = () => {
    setStep('upload')
    setCsv('')
    setFileName(null)
    setHeaders([])
    setMapping({})
    setPreview(null)
    setResult(null)
    setSavedId('')
    setOutcomeFilter('')
    setError(null)
    setInfo(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const runPreview = useCallback(
    async (text: string, name: string | null, map: Record<string, string> | null) => {
      setBusy(true)
      setError(null)
      try {
        const p = await importsApi.preview(kind, text, map, name)
        setPreview(p)
        setMapping(p.appliedMapping)
        return p
      } catch (e) {
        setError(errMessage(e))
        return null
      } finally {
        setBusy(false)
      }
    },
    [kind],
  )

  const onFile = async (file: File) => {
    setError(null)
    setInfo(null)
    if (file.size > MAX_FILE_BYTES) {
      setError(`File troppo grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Limite massimo: 5 MB.`)
      return
    }
    // F-140 (REQ-064-004): un .xlsx passa da POST /api/spreadsheets/to-csv e torna come testo CSV; l'anteprima e' quella di sempre.
    let text: string
    try {
      text = await readTabularFile(file)
    } catch (e) {
      setError(errMessage(e))
      return
    }
    setCsv(text)
    setFileName(file.name)
    setHeaders(readHeaders(text))
  }

  const goToMapping = async () => {
    const chosen = saved.find(s => String(s.id) === savedId)
    const p = await runPreview(csv, fileName, chosen ? chosen.mapping : null)
    if (p) setStep('mapping')
  }

  const refreshPreview = async () => {
    const clean: Record<string, string> = {}
    for (const [h, f] of Object.entries(mapping)) if (f !== NO_FIELD) clean[h] = f
    const p = await runPreview(csv, fileName, clean)
    if (p) setStep('preview')
  }

  const saveMapping = async () => {
    const name = mapName.trim()
    if (!name) {
      setError('Dai un nome alla mappatura da salvare.')
      return
    }
    const clean: Record<string, string> = {}
    for (const [h, f] of Object.entries(mapping)) if (f !== NO_FIELD) clean[h] = f
    setBusy(true)
    setError(null)
    try {
      const existing = saved.find(s => s.isOwner && s.name === name)
      if (existing) await importMappingsApi.update(existing.id, { kind, name, mapping: clean, shared: mapShared })
      else await importMappingsApi.create({ kind, name, mapping: clean, shared: mapShared })
      setInfo(`Mappatura "${name}" salvata.`)
      setMapName('')
      await loadKindData(kind)
    } catch (e) {
      setError(errMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      setResult(await importsApi.apply(kind, preview.importId))
      setStep('done')
    } catch (e) {
      setError(errMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const fieldLabel = useMemo(() => {
    const map: Record<string, string> = {}
    for (const f of fields) map[f.key] = f.label
    return map
  }, [fields])

  const importable = preview?.byOutcome['Importable'] ?? 0
  const visibleRows = (preview?.rows ?? []).filter(r => outcomeFilter === '' || r.outcome === outcomeFilter)

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}
      {info && <Callout tone="success">{info}</Callout>}

      <ol className="breadcrumb md-typescale-body-small mb-0">
        {(
          [
            ['upload', '1. File'],
            ['mapping', '2. Mappatura'],
            ['preview', '3. Anteprima e validazione'],
            ['done', '4. Conferma'],
          ] as [Step, string][]
        ).map(([s, label]) => (
          <li key={s} className={'breadcrumb-item' + (s === step ? ' active md-typescale-emphasized-body-medium' : '')}>
            {label}
          </li>
        ))}
      </ol>

      {step === 'upload' && (
        <Card variant="outlined">
          <CardContent>
            <FormGrid columns={3}>
              <div>
                <Select label="Cosa importi" value={kind} onChange={e => setKind(e.target.value as ImportKind)}>
                  <option value="customers">Clienti</option>
                  <option value="leads">Lead</option>
                </Select>
              </div>
              <div className="md-span-2">
                <TextField
                  ref={fileRef as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
                  label={`File CSV${importExcel ? ' o Excel (.xlsx)' : ''} (massimo 5 MB, separatore , o ;)`}
                  type="file"
                  accept={importExcel ? EXCEL_ACCEPT : CSV_ACCEPT}
                  disabled={busy}
                  onChange={e => {
                    const f = (e.currentTarget as HTMLInputElement).files?.[0]
                    if (f) void onFile(f)
                  }}
                />
              </div>
              {saved.length > 0 && (
                <div className="md-span-2">
                  <Select label="Mappatura salvata (facoltativa)" value={savedId} onChange={e => setSavedId(e.target.value)}>
                    <option value="">Riconosci le colonne automaticamente</option>
                    {saved.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {s.shared ? ' (condivisa)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </FormGrid>
            {requiredRule && <p className="text-muted md-typescale-body-small mt-3 mb-0">{requiredRule}</p>}
            {headers.length > 0 && (
              <p className="md-typescale-body-small mt-3 mb-0">
                <strong>{fileName}</strong>: {headers.length} colonne riconosciute nell’intestazione.
              </p>
            )}
            <Button variant="filled" className="mt-3" disabled={busy || csv === ''} onClick={() => void goToMapping()}>
              {busy ? 'Analisi…' : 'Avanti'}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 'mapping' && preview && (
        <Card variant="outlined">
          <CardHeader title="Associa le colonne del file ai campi" headingLevel={3} />
          <CardContent>
            <p className="text-muted md-typescale-body-small">
              Le colonne sono state riconosciute automaticamente: correggile dove serve. Un campo può essere
              associato a una sola colonna.
            </p>
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Colonna del file</th>
                  <th>Campo</th>
                </tr>
              </thead>
              <tbody>
                {headers.map(h => (
                  <tr key={h}>
                    <td className="font-monospace md-typescale-body-small">{h}</td>
                    <td>
                      <Select
                        label={h}
                        labelHidden
                        value={mapping[h] ?? NO_FIELD}
                        onChange={e => setMapping(prev => ({ ...prev, [h]: e.target.value }))}
                      >
                        <option value={NO_FIELD}>— ignora —</option>
                        {fields.map(f => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>

            <Card variant="filled" padding="sm" className="my-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div className="md-row-field">
                  <TextField
                    label="Salva questa mappatura"
                    supportingText="Nome (es. Fornitore Rossi)"
                    maxLength={100}
                    value={mapName}
                    onChange={e => setMapName(e.target.value)}
                  />
                </div>
                {isAdmin(auth) && (
                  <div>
                    <Checkbox
                      id="map-shared"
                      label="Condividi con tutti"
                      checked={mapShared}
                      onChange={e => setMapShared(e.target.checked)}
                    />
                  </div>
                )}
                <div>
                  <Button variant="outlined" size="sm" disabled={busy} onClick={() => void saveMapping()}>
                    Salva mappatura
                  </Button>
                </div>
              </div>
            </Card>

            <div className="d-flex gap-2">
              <Button variant="text" onClick={reset}>
                Ricomincia
              </Button>
              <Button variant="filled" disabled={busy} onClick={() => void refreshPreview()}>
                {busy ? 'Analisi…' : 'Mostra anteprima'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'preview' && preview && (
        <Card variant="outlined">
          <CardHeader
            title={`Anteprima: ${preview.processedRowCount} righe analizzate su ${preview.totalRowsInFile}`}
            headingLevel={3}
          />
          <CardContent>
            {preview.truncated && (
              <Callout tone="warning">
                Il file supera il limite di {preview.maxRowsPerImport} righe per importazione: vengono analizzate
                e importate solo le prime {preview.maxRowsPerImport}. Dividi il file per importare il resto.
              </Callout>
            )}
            <div className="my-3 d-flex flex-wrap gap-2">
              {(Object.keys(OUTCOME_LABELS) as ImportRowOutcome[]).map(o => (
                <Button
                  key={o}
                  variant={outcomeFilter === o ? 'filled' : 'outlined'}
                  size="sm"
                  onClick={() => setOutcomeFilter(outcomeFilter === o ? '' : o)}
                >
                  {OUTCOME_LABELS[o]}: {preview.byOutcome[o] ?? 0}
                </Button>
              ))}
            </div>
            <DataTable variant="plain" stickyHeader maxHeight="420px">
              <thead>
                <tr>
                  <th>Riga</th>
                  <th>Esito</th>
                  <th>Dati</th>
                  <th>Problemi, duplicati e avvisi</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.slice(0, 500).map(r => (
                  <tr key={r.rowNumber}>
                    <td className="md-num">{r.rowNumber}</td>
                    <td>
                      <StatusPill tone={OUTCOME_TONES[r.outcome]}>{OUTCOME_LABELS[r.outcome]}</StatusPill>
                    </td>
                    <td className="md-typescale-body-small">
                      {Object.entries(r.values)
                        .filter(([, v]) => v !== null && v !== undefined && v !== '')
                        .map(([k, v]) => `${fieldLabel[k]?.split(' (')[0] ?? k}: ${String(v)}`)
                        .join(' · ')}
                    </td>
                    <td className="md-typescale-body-small">
                      {r.problems.map(p => (
                        <div key={p} className="text-danger">
                          {PROBLEM_LABELS[p] ?? p}
                        </div>
                      ))}
                      {(r.duplicates ?? []).map((d, i) => (
                        <div key={`d${i}`} className="text-warning-emphasis">
                          Duplicato su {FIELD_LABELS[d.field] ?? d.field}
                          {d.otherRow ? ` (come la riga ${d.otherRow})` : ''}
                          {d.existingId ? ` (già presente, ${d.where} #${d.existingId})` : ''}
                        </div>
                      ))}
                      {(r.warnings ?? []).map((d, i) => (
                        <div key={`w${i}`} className="text-muted">
                          Avviso: {FIELD_LABELS[d.field] ?? d.field} già noto
                          {d.otherRow ? ` (riga ${d.otherRow})` : ''}
                          {d.existingId ? ` (${d.where} #${d.existingId})` : ''}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {visibleRows.length > 500 && (
              <p className="md-typescale-body-small text-muted">Mostrate le prime 500 righe su {visibleRows.length}.</p>
            )}

            <div className="d-flex align-items-center gap-2 mt-3">
              <Button variant="outlined" onClick={() => setStep('mapping')}>
                Modifica mappatura
              </Button>
              <Button variant="filled" disabled={busy || importable === 0} onClick={() => void apply()}>
                {busy ? 'Importazione…' : `Conferma importazione di ${importable} righe`}
              </Button>
              {importable === 0 && <span className="text-muted md-typescale-body-small">Nessuna riga importabile.</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'done' && result && (
        <Card variant="outlined">
          <CardHeader title="Importazione completata" headingLevel={3} />
          <CardContent>
            <p>
              Creati {result.createdLeadCount ?? result.createdCustomerCount ?? 0}{' '}
              {kind === 'leads' ? 'lead' : 'clienti'}. ID lotto: <code>{result.batchId}</code>
            </p>
            <p className="text-muted md-typescale-body-small">
              Se hai sbagliato file, l’Admin può annullare l’intero lotto dallo storico, purché nessun dato
              dipenda già dai record importati.
            </p>
            <div className="d-flex gap-2">
              <Button variant="outlined" onClick={reset}>
                Nuova importazione
              </Button>
              <Button variant="filled" onClick={onDone}>
                Vai allo storico lotti
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </Page>
  )
}

/* ───────────────────────────── Storico lotti ───────────────────────────── */

function HistoryTab({ auth }: { auth: LoginResponse }) {
  const [items, setItems] = useState<ImportBatch[]>([])
  const [kind, setKind] = useState<ImportKind | ''>('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [deps, setDeps] = useState<ImportBatchDependencies | null>(null)
  const [undoTarget, setUndoTarget] = useState<ImportBatch | null>(null)
  const [reason, setReason] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const filters: { kind?: ImportKind; status?: string } = {}
      if (kind) filters.kind = kind
      if (status) filters.status = status
      setItems((await importsApi.listBatches(filters)).items)
    } catch (e) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [kind, status])

  useEffect(() => {
    void load()
  }, [load])

  const showDeps = async (b: ImportBatch) => {
    setError(null)
    setInfo(null)
    try {
      setDeps(await importsApi.dependencies(b.kind, b.id))
    } catch (e) {
      setError(errMessage(e))
    }
  }

  const startUndo = async (b: ImportBatch) => {
    setError(null)
    setInfo(null)
    setReason('')
    try {
      const d = await importsApi.dependencies(b.kind, b.id)
      setDeps(d)
      if (d.totalDependencies > 0) {
        setUndoTarget(null)
        setError('Il lotto non può essere annullato: altri dati dipendono dai suoi record.')
        return
      }
      setUndoTarget(b)
    } catch (e) {
      setError(errMessage(e))
    }
  }

  const confirmUndo = async () => {
    if (!undoTarget) return
    if (!reason.trim()) {
      setError('Indica il motivo dell’annullamento.')
      return
    }
    setError(null)
    try {
      const r = await importsApi.undo(undoTarget.kind, undoTarget.id, reason.trim())
      setInfo(`Lotto annullato: ${r.deletedRecords} record rimossi.`)
      setUndoTarget(null)
      setDeps(null)
      await load()
    } catch (e) {
      setError(errMessage(e))
      setUndoTarget(null)
      await load()
    }
  }

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}
      {info && <Callout tone="success">{info}</Callout>}

      <div className="d-flex flex-wrap gap-2">
        <div>
          <Select label="Tipo di lotto" labelHidden value={kind} onChange={e => setKind(e.target.value as ImportKind | '')}>
            <option value="">Tutti i tipi</option>
            <option value="leads">Lead</option>
            <option value="customers">Clienti</option>
          </Select>
        </div>
        <div>
          <Select label="Stato del lotto" labelHidden value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Tutti gli stati</option>
            <option value="Applicato">Applicato</option>
            <option value="Annullato">Annullato</option>
          </Select>
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <Callout tone="info">Nessun lotto di importazione.</Callout>
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Data</th>
              <th>Tipo</th>
              <th>File</th>
              <th className="text-end">Righe</th>
              <th className="text-end">Importate</th>
              <th className="text-end">Duplicate</th>
              <th className="text-end">Scartate</th>
              <th>Stato</th>
              <th>ID lotto</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.id}>
                <td className="md-typescale-body-small">{fmtDateTime(b.appliedAt)}</td>
                <td>{IMPORT_KIND_LABELS[b.kind]}</td>
                <td className="md-typescale-body-small">{b.fileName ?? '—'}</td>
                <td className="text-end md-num">{b.totalRowsInFile}</td>
                <td className="text-end md-num">{b.importedCount}</td>
                <td className="text-end md-num">{b.duplicateCount}</td>
                <td className="text-end md-num">{b.invalidCount}</td>
                <td>
                  <StatusPill tone={b.status === 'Annullato' ? 'neutral' : 'success'}>{b.status}</StatusPill>
                  {b.status === 'Annullato' && (
                    <div className="md-typescale-body-small text-muted">
                      {fmtDateTime(b.undoneAt)} — {b.undoReason}
                    </div>
                  )}
                </td>
                <td className="font-monospace md-typescale-body-small">{b.id.slice(0, 8)}</td>
                <td className="text-nowrap">
                  <Button variant="outlined" size="sm" className="me-1" onClick={() => void showDeps(b)}>
                    Dipendenze
                  </Button>
                  {isAdmin(auth) && b.status === 'Applicato' && (
                    <Button variant="outlined" size="sm" onClick={() => void startUndo(b)}>
                      Annulla lotto
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {deps && (
        <Card variant="outlined">
          <CardHeader
            title={`Lotto ${deps.id.slice(0, 8)} (${IMPORT_KIND_LABELS[deps.kind]}): ${deps.recordCount} record`}
            headingLevel={3}
          />
          <CardContent>
            {deps.totalDependencies === 0 ? (
              <p className="mb-0">Nessun dato dipende da questo lotto: può essere annullato per intero.</p>
            ) : (
              <>
                <p>Questi dati dipendono dai record del lotto, che quindi non può essere annullato:</p>
                <ul className="mb-0">
                  {deps.dependencies.map(d => (
                    <li key={d.area}>
                      {d.area}: <strong>{d.count}</strong>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {undoTarget && (
        <Card variant="outlined">
          <CardHeader
            title={<span className="text-danger">Annulla il lotto {undoTarget.id.slice(0, 8)}</span>}
            headingLevel={3}
          />
          <CardContent>
            <p className="md-typescale-body-small">
              Verranno cancellati tutti i {undoTarget.importedCount} record del lotto. L’operazione non si può
              ripetere né parzializzare; il lotto resta nello storico come “Annullato”.
            </p>
            <TextField
              label="Motivo dell’annullamento (obbligatorio)"
              maxLength={500}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
            <div className="d-flex gap-2 mt-3">
              <Button variant="text" onClick={() => setUndoTarget(null)}>
                Indietro
              </Button>
              <Button variant="text" tone="error" onClick={() => void confirmUndo()}>
                Conferma annullamento
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </Page>
  )
}
